import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ToolStreamingStatus, UniversalToolCall } from '@app-libs/model';


/**
 * ToolStatusComponent - Displays tool execution status in chat messages
 * 
 * Display Modes:
 * - inline: Shows tool count with icon
 * - compact: Shows tool names 
 * - detail: Shows comprehensive tool list with individual statuses
 * - off: Hidden
 * 
 * Status Types:
 * - idle: No tools or waiting
 * - executing: Tools currently running (spinning icon)
 * - completed: Tools finished successfully (check icon)
 * - failed: Tool execution failed (error icon)
 * - used: Tools were used (frontend-only status for historical messages)
 */
@Component({
  selector: 'app-tool-status',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule],
  templateUrl: './tool-status.component.html',
  styleUrls: ['./tool-status.component.scss']
})
export class ToolStatusComponent {
  @Input() set toolCalls(value: UniversalToolCall[]) {
    // Validate and normalize input
    if (Array.isArray(value)) {
      this._toolCalls = value.filter(tool => tool && tool.name);
    } else {
      this._toolCalls = [];
    }
    console.debug('ToolStatusComponent: Processed tool calls:', this._toolCalls.length);
  }
  get toolCalls(): UniversalToolCall[] {
    return this._toolCalls;
  }
  private _toolCalls: UniversalToolCall[] = [];

  @Input() displayMode: 'inline' | 'compact' | 'detail' | 'off' = 'compact';
  @Input() status: ToolStreamingStatus | 'used' = 'used'; // 'used' is a special frontend-only status

  get displayClass(): string {
    return `tool-status-${this.displayMode} status-${this.status}`;
  }

  get icon(): string {
    switch (this.status) {
      case ToolStreamingStatus.IDLE:
        return 'pending';
      case ToolStreamingStatus.EXECUTING:
        return 'sync';
      case ToolStreamingStatus.COMPLETED:
        return 'check_circle';
      case ToolStreamingStatus.FAILED:
        return 'error';
      case 'used':
      default:
        return 'build_circle';
    }
  }

  getToolName(tool: UniversalToolCall): string {
    return tool?.name || 'tool';
  }

  get shouldDisplay(): boolean {
    return this.displayMode !== 'off' &&
      (this.toolCalls.length > 0 || this.status === 'executing');
  }

  get displayText(): string {
    if (this.status === 'executing' && this.toolCalls.length === 0) {
      return 'Using tools...';
    }
    const count = this.toolCalls.length;
    return `${count} tool${count !== 1 ? 's' : ''} ${this.getStatusText()}`;
  }

  get tooltip(): string {
    switch (this.status) {
      case 'executing':
        return 'Tools are currently running';
      case 'completed':
        return 'Tools completed successfully';
      case 'failed':
        return 'Tool execution failed';
      case 'used':
        return `Used ${this.toolCalls.length} tool${this.toolCalls.length !== 1 ? 's' : ''}`;
      default:
        return '';
    }
  }

  getStatusText(): string {
    switch (this.status) {
      case 'executing':
        return 'running';
      case 'completed':
        return 'completed';
      case 'failed':
        return 'failed';
      case 'used':
      default:
        return 'used';
    }
  }

  /**
   * Get icon for individual tool in detail view
   */
  getToolIcon(): string {
    if (this.status === 'executing') {
      return 'sync';
    }
    if (this.status === 'failed') {
      return 'error_outline';
    }
    return 'check_circle';
  }

  /**
   * Get status text for individual tool in detail view
   */
  getToolItemStatus(): string {
    if (this.status === 'executing') {
      return 'running...';
    }
    if (this.status === 'failed') {
      return 'failed';
    }
    if (this.status === 'completed') {
      return 'complete';
    }
    return '';
  }
}